import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import type { FC, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { INK, INK_BLACK, NEUTRAL } from "@/theme/tokens";
import { SystemDatePickerPopup } from "@/ui/Popups/SystemDatePickerPopup";
import { ApplyFromDateFieldV2 } from "./ApplyFromDateFieldV2";
import { type LocationEditorDesign, smallLabelTypographySx, V1_APPLY_FROM_FONT_SIZE, V1_APPLY_FROM_PADDING, V1_FIELD_BORDER_RADIUS } from "./consts";

export type ApplyFromDateFieldCommonProps = {
	/** The first day the save rewrites existing shifts from. */
	updateFromDate: DateTime;
	onUpdateFromDateChange: (date: DateTime | null) => void;
	/** False when the save can't reach a shift, which makes the date inert — so it can't be picked. */
	affectsShifts: boolean;
	/** The open date picker's anchor, or null while it's shut. */
	datePickerAnchorEl: HTMLElement | null;
	onDatePickerAnchorElChange: (anchorEl: HTMLElement | null) => void;
};

/**
 * `v1`: the Schedule Template popup's "Set the affect date" box. `v2`: a settings-panel line (the Locations page's
 * expanded editor), which alone has a read-only state, under a Schedule Template edit lock.
 */
export type ApplyFromDateFieldProps = ApplyFromDateFieldCommonProps &
	(
		| { design: Extract<LocationEditorDesign, "v1"> }
		| {
				design: Extract<LocationEditorDesign, "v2">;
				disabled: boolean;
				/** The department's timezone: the earliest pick is today there, not on the admin's machine. */
				timezone: string;
		  }
	);

/** The date an edit starts applying to existing shifts from. */
export const ApplyFromDateField: FC<ApplyFromDateFieldProps> = (props): ReactElement => {
	const { updateFromDate, onUpdateFromDateChange, affectsShifts, datePickerAnchorEl, onDatePickerAnchorElChange } = props;
	const { t } = useTranslation();
	switch (props.design) {
		case "v1":
			return (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 0.5,
						alignItems: "start",
					}}
				>
					{/* Exact English is an e2e selector (tests/helpers/masterScheduleEffects.ts). */}
					<Typography sx={smallLabelTypographySx}>{t("set.the.affect.date")}</Typography>
					{/* Disabled when the save cannot reach a shift: the server skips the cascade, so the date
					    would be accepted and then ignored. The tooltip needs an enabled wrapper element to
					    receive the hover, hence InHouseTooltip around the box rather than on it. */}
					<InHouseTooltip title={affectsShifts ? "" : t("schedule.template.applyFrom.noShiftsAffected")}>
						<Box
							data-testid="location-apply-from-date"
							aria-disabled={!affectsShifts}
							sx={{
								padding: V1_APPLY_FROM_PADDING,
								border: `1px solid ${NEUTRAL[400]}`,
								borderRadius: V1_FIELD_BORDER_RADIUS,
								fontSize: V1_APPLY_FROM_FONT_SIZE,
								color: affectsShifts ? INK.SECONDARY : alpha(INK.SECONDARY, 0.4),
								cursor: affectsShifts ? "pointer" : "default",
								...(affectsShifts && {
									":hover": {
										backgroundColor: alpha(INK_BLACK, 0.01),
									},
								}),
							}}
							onClick={(event) => {
								event.stopPropagation();
								if (!affectsShifts) {
									return;
								}
								if (!datePickerAnchorEl) {
									onDatePickerAnchorElChange(event.currentTarget);
								} else {
									onDatePickerAnchorElChange(null);
								}
							}}
						>
							{t("schedule.template.v3.editor.updateFrom", { date: updateFromDate.toFormat("D") })}
							{datePickerAnchorEl && (
								<SystemDatePickerPopup
									anchorEl={datePickerAnchorEl}
									open={!!datePickerAnchorEl}
									onClose={() => onDatePickerAnchorElChange(null)}
									onChange={onUpdateFromDateChange}
									value={updateFromDate}
									overridePickerProps={{
										minDate: DateTime.now().startOf("day"),
									}}
									disablePortal
								/>
							)}
						</Box>
					</InHouseTooltip>
				</Box>
			);
		case "v2":
			return (
				<ApplyFromDateFieldV2
					updateFromDate={updateFromDate}
					onUpdateFromDateChange={onUpdateFromDateChange}
					affectsShifts={affectsShifts}
					datePickerAnchorEl={datePickerAnchorEl}
					onDatePickerAnchorElChange={onDatePickerAnchorElChange}
					disabled={props.disabled}
					timezone={props.timezone}
				/>
			);
	}
};
